import { Component, type ReactNode } from 'react';
import { motion } from 'framer-motion';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('ErrorBoundary caught:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center justify-center min-h-[60vh] bg-wonder-cream"
                >
                    <div className="text-center max-w-md mx-auto px-4">
                        <div className="text-6xl mb-6">🍃</div>
                        <h2 className="font-display text-3xl text-wonder-forest mb-3">
                            Something went wrong
                        </h2>
                        <p className="text-wonder-charcoal/60 mb-6">
                            We encountered an unexpected error. Please try refreshing the page.
                        </p>
                        <button
                            onClick={() => {
                                this.setState({ hasError: false, error: null });
                                window.location.reload();
                            }}
                            className="btn-primary"
                        >
                            Refresh Page
                        </button>
                    </div>
                </motion.div>
            );
        }

        return this.props.children;
    }
}
